import { RecentQuestionsResponse } from '@/types/services';
import { ChatSession, ERole, type ChatMessage } from '@/types/chat';

export const delay = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    signal?.addEventListener('abort', onAbort, { once: true });
  });

export const MOCK_RECENT_QUESTIONS: { questions: string[] } = {
  questions: [
    '昨日不同订单类型笔数分布',
    '最近7天结算订单数趋势',
    '本月各门店销售额排名',
    '查询今日退款订单详情',
  ],
};

export const MOCK_RECOMMEND_QUESTIONS: string[] = [
  '这些数据和上周相比如何？',
  '哪个时段订单量最高？',
  '能帮我生成一份数据报表吗？',
  '各支付方式的占比是多少？',
];

export const MOCK_SESSION = {
  access_token: 'mock-access-token-abc123',
  chat_id: 100001,
};

const MOCK_HISTORY_BIZ = {
  role: ERole.ANALYST,
  accessToken: MOCK_SESSION.access_token,
  chatId: MOCK_SESSION.chat_id,
};

function createMockHistorySession(
  id: string,
  title: string,
  updatedAt: number,
  reply: string,
): ChatSession {
  const userMsg: ChatMessage = {
    id: `${id}-user`,
    role: 'user',
    type: 'default',
    bizPayload: MOCK_HISTORY_BIZ,
    content: { text: title },
    status: 'success',
    timestamp: updatedAt,
  };
  const assistantMsg: ChatMessage = {
    id: `${id}-assistant`,
    role: 'assistant',
    type: 'default',
    bizPayload: MOCK_HISTORY_BIZ,
    content: { text: reply },
    status: 'success',
    timestamp: updatedAt + 1000,
  };
  return {
    id,
    title,
    createdAt: updatedAt,
    updatedAt,
    messages: [userMsg, assistantMsg],
  };
}

const today = Date.now();
const olderDay = new Date('2026-03-17T10:00:00').getTime();

/** 历史对话 MOCK，结构与 ChatSession 一致，接口就绪后可直接替换 */
export const MOCK_HISTORY_SESSIONS: ChatSession[] = [
  createMockHistorySession(
    'mock-history-1',
    '查询最近7天的订单金额趋势并用折线图展示',
    today - 10 * 60 * 1000,
    '已为你查询近 7 天订单金额趋势，整体呈上升走势。',
  ),
  createMockHistorySession(
    'mock-history-2',
    '最近30天结算的订单数',
    today - 30 * 60 * 1000,
    '近 30 天共结算 8,642 笔订单。',
  ),
  createMockHistorySession(
    'mock-history-3',
    '查询门店的结算订单数量并用柱状图展示',
    today - 50 * 60 * 1000,
    '已按门店汇总结算订单数量。',
  ),
  createMockHistorySession(
    'mock-history-4',
    '查询最近7天的订单金额趋势并用折线图展示',
    olderDay,
    '已为你查询近 7 天订单金额趋势。',
  ),
  createMockHistorySession(
    'mock-history-5',
    '查询门店的结算订单数量并用柱状图展示',
    olderDay - 2 * 60 * 60 * 1000,
    '已按门店汇总结算订单数量。',
  ),
];

// ─── Mock 场景集合 ────────────────────────────────────────────────────────────

/** 场景 1：饼图 — 订单类型分布 */
export const MOCK_PIE_CHART: RecentQuestionsResponse = {
  type: 'data_chart',
  success: true,
  record_id: 200001,
  title: '昨日订单类型分布',
  content:
    '根据最新数据，昨日订单总量为 12,580 笔，外卖占比 45%，堂食 35%，外带 20%，整体较上周同期增长 8.3%。',
  data: [
    { category: '外卖', cnt: 5661 },
    { category: '堂食', cnt: 4403 },
    { category: '外带', cnt: 2516 },
  ],
  chart: {
    type: 'pie',
    title: '昨日订单类型分布',
    dimensions: [{ code: 'category', name: '类别' }],
    metrics: [{ code: 'cnt', name: '数值' }],
  },
};

/** 场景 2：折线图 — 近 7 天结算订单趋势 */
export const MOCK_LINE_CHART: RecentQuestionsResponse = {
  type: 'data_chart',
  success: true,
  record_id: 200002,
  title: '近 7 天结算订单趋势',
  content: '近 7 天结算订单整体呈上升趋势，周末峰值明显，周五达到本周最高点 3,210 笔。',
  sql: 'SELECT date, COUNT(*) AS cnt FROM orders WHERE date >= CURDATE() - 7 AND status = "settled" GROUP BY date ORDER BY date',
  data: [
    { date: '04/15', income: 2100, expense: 1000 },
    { date: '04/16', income: 2340, expense: 1200 },
    { date: '04/17', income: 2780, expense: 1400 },
    { date: '04/18', income: 2560, expense: 1600 },
    { date: '04/19', income: 3210, expense: 1800 },
    { date: '04/20', income: 3050, expense: 2000 },
    { date: '04/21', income: 2890, expense: 2200 },
  ],
  chart: {
    type: 'line',
    title: '近 7 天结算订单趋势',
    dimensions: [{ code: 'date', name: '日期' }],
    metrics: [
      { code: 'income', name: '收入' },
      { code: 'expense', name: '支出' },
    ],
  },
};

/** 场景 3：柱状图 — 本月门店销售额排名 */
export const MOCK_BAR_CHART: RecentQuestionsResponse = {
  type: 'data_chart',
  success: true,
  record_id: 200003,
  title: '本月门店销售额排名 TOP 5',
  content: '本月销售额 TOP 5 门店中，朝阳旗舰店以 128 万元位居第一，较上月增长 12%。',
  sql: 'SELECT store_name, SUM(amount) AS total FROM orders WHERE MONTH(date) = MONTH(CURDATE()) GROUP BY store_name ORDER BY total DESC LIMIT 5',
  data: [
    { store_name: '朝阳旗舰店', total: 1280000 },
    { store_name: '海淀科技店', total: 1050000 },
    { store_name: '浦东新区店', total: 980000 },
    { store_name: '天河城店', total: 870000 },
    { store_name: '武汉光谷店', total: 760000 },
  ],
  chart: {
    type: 'bar',
    title: '本月门店销售额排名 TOP 5',
    dimensions: [{ code: 'store_name', name: '门店名称' }],
    metrics: [{ code: 'total', name: '销售额' }],
  },
};

/** 场景 4：纯表格（chart.type = 'table'）— 今日退款订单明细 */
export const MOCK_TABLE: RecentQuestionsResponse = {
  type: 'data_chart',
  success: true,
  record_id: 200004,
  title: '今日退款订单明细',
  content: '今日共发生退款订单 18 笔，合计退款金额 ¥4,320，主要集中在外卖品类。',
  sql: 'SELECT order_id, store_name, amount, reason, created_at FROM refund_orders WHERE DATE(created_at) = CURDATE()',
  data: [
    {
      order_id: 'ORD-20240421-001',
      store_name: '朝阳旗舰店',
      amount: 368,
      reason: '商品缺货',
      created_at: '09:12',
    },
    {
      order_id: 'ORD-20240421-002',
      store_name: '海淀科技店',
      amount: 128,
      reason: '用户取消',
      created_at: '10:05',
    },
    {
      order_id: 'ORD-20240421-003',
      store_name: '浦东新区店',
      amount: 256,
      reason: '配送超时',
      created_at: '11:33',
    },
    {
      order_id: 'ORD-20240421-004',
      store_name: '天河城店',
      amount: 480,
      reason: '商品质量',
      created_at: '13:47',
    },
    {
      order_id: 'ORD-20240421-005',
      store_name: '武汉光谷店',
      amount: 200,
      reason: '用户取消',
      created_at: '14:22',
    },
  ],
  chart: { type: 'table', title: '今日退款订单明细' },
};

/** 场景 5：纯文本 — 政策/说明类回答 */
export const MOCK_TEXT: RecentQuestionsResponse = {
  type: 'text',
  success: true,
  record_id: 200005,
  title: '订单结算说明',
  content:
    '订单结算通常在交易完成后 T+1 个工作日内完成。节假日期间可能延迟 1-2 个工作日。如遇异常可联系商务支持团队处理。\n\n结算金额 = 订单金额 - 平台服务费 - 退款金额。平台服务费按照合同约定费率计算，具体费率请参考您的合作协议。',
};

/** 场景 6：错误响应 */
export const MOCK_ERROR: RecentQuestionsResponse = {
  type: 'error',
  success: false,
  record_id: 200006,
  message: '暂时无法查询该数据，请稍后重试或联系管理员。',
};
/** 分段模式第二阶段 mock：recordChat 返回图表数据 */
export const MOCK_RECORD_CHART = {
  success: true,
  record_id: 300001,
  chat_record_id: 300001,
  chart: {
    type: 'bar' as const,
    title: '近7天订单汇总',
    dimensions: [{ code: '日期', name: '日期' }],
    metrics: [
      { code: '订单数', name: '订单数' },
      { code: '销售额（元）', name: '销售额（元）' },
    ],
  },
  data: [
    { 日期: '2024-04-21', 订单数: 328, '销售额（元）': 45600 },
    { 日期: '2024-04-20', 订单数: 302, '销售额（元）': 41200 },
    { 日期: '2024-04-19', 订单数: 356, '销售额（元）': 49800 },
    { 日期: '2024-04-18', 订单数: 289, '销售额（元）': 38900 },
    { 日期: '2024-04-17', 订单数: 412, '销售额（元）': 58300 },
    { 日期: '2024-04-16', 订单数: 378, '销售额（元）': 52100 },
    { 日期: '2024-04-15', 订单数: 291, '销售额（元）': 40500 },
  ],
};
/**
 * 随机场景池：文本 × 2 权重 + 图表/表格各 × 1，error 场景单独保留供手动测试。
 * 调整数组内容可改变各场景出现概率。
 */
export const MOCK_SCENARIOS: RecentQuestionsResponse[] = [
  MOCK_TEXT,
  MOCK_TEXT, // 文本出现概率 2/6
  MOCK_PIE_CHART,
  MOCK_LINE_CHART,
  MOCK_BAR_CHART,
  MOCK_TABLE,
];

/** 随机返回一个 mock 场景 */
export const nextMockScenario = (): RecentQuestionsResponse =>
  MOCK_SCENARIOS[Math.floor(Math.random() * MOCK_SCENARIOS.length)];

/** 随机失败概率（0~1），30% 概率模拟接口失败 */
export const MOCK_FAIL_RATE = 0.3;

/** 随机决定是否模拟失败，失败时直接 throw */
export const maybeFailMock = () => {
  if (Math.random() < MOCK_FAIL_RATE) {
    throw new Error('Mock: 随机模拟接口失败');
  }
};

/** 总结数据 mock */
export const MOCK_SUMMARIZE = {
  success: true,
  chat_record_id: 200099,
  content: `### 数据分析结果\n\n**1. 数据概况**\n*   **时间范围**：2026 年 5 月 1 日 至 2026 年 5 月 7 日（共 7 天）。\n*   **指标**：总营收 (total_revenue)。\n*   **数据总量**：7 条记录。\n\n**2. 核心统计指标**\n*   **总营收合计**：1,349,500,657.89 元\n*   **日均营收**：192,785,808.27 元\n*   **最高营收日**：2026-05-01，金额为 240,070,231.56 元\n*   **最低营收日**：2026-05-05，金额为 163,147,545.92 元\n*   **极差（最高 - 最低）**：76,922,685.64 元\n\n**3. 趋势分析**\n*   **整体走势**：本周营收呈现“先降后升”的波动趋势。\n    *   **下降阶段**：从 5 月 1 日（周一）的峰值开始，营收连续 4 天逐日下滑，直至 5 月 5 日（周五）达到本周最低点。累计跌幅约为 32.0%。\n    *   **回升阶段**：5 月 6 日（周六）营收出现显著反弹，单日环比增长约 16.5%；5 月 7 日（周日）略有回落，但仍高于周五水平。\n*   **周中 vs 周末**：\n    *   期初（5 月 1 日）表现最强，可能受月初或假期因素影响。\n    *   周中（5 月 4 日 -5 月 5 日）为业务低谷期。\n    *   周末（5 月 6 日 -5 月 7 日）客流或消费有所恢复，但未回到期初高点。\n\n**4. 每日详细数据表**\n\n| 业务日期 | 总营收 (元) | 环比变化 |\n| :--- | :--- | :--- |\n| 2026-05-01 | 240,070,231.56 | - |\n| 2026-05-02 | 203,441,689.99 | ▼ 15.26% |\n| 2026-05-03 | 192,582,903.07 | ▼ 5.34% |\n| 2026-05-04 | 181,033,655.75 | ▼ 6.00% |\n| 2026-05-05 | 163,147,545.92 | ▼ 9.88% |\n| 2026-05-06 | 190,050,558.44 | ▲ 16.49% |\n| 2026-05-07 | 179,172,073.16 | ▼ 5.72% |`,
};

/** 分段模式 mock：sqlDataChat 返回格式 */
export const MOCK_SQL_DATA = {
  chat_record_id: 1234,
  sql: 'SELECT date, COUNT(*) AS order_cnt, SUM(amount) AS revenue FROM orders GROUP BY date ORDER BY date DESC LIMIT 7',
  data: {
    fields: ['日期', '订单数', '销售额（元）'],
    data: [
      ['2024-04-21', '328', '45600'],
      ['2024-04-20', '302', '41200'],
      ['2024-04-19', '356', '49800'],
      ['2024-04-18', '289', '38900'],
      ['2024-04-17', '412', '58300'],
      ['2024-04-16', '378', '52100'],
      ['2024-04-15', '291', '40500'],
    ],
  },
  title: '近7天订单汇总',
};

// 门店经营分析报告
export const MOCK_REPORT = {
  success: true,
  chat_record_id: 9890,
  content: `# 门店经营分析报告: 示例门店
> **门店编号:** S002601092857024246   **门店名称:** 示例门店   **门店类型:** MINI_MART   **经营状态:** ONLINE   **生成日期:** 2026-05-04   **数据来源:** ba_dwd_fact_* (DWD 明细层), 排除 DWS 汇总层

---

## 一、执行摘要

示例门店 虽然注册为 MINI_MART 类型，但从实际经营数据来看，该门店更接近 **酒水饮品消费场所**（类似社区酒吧/小酒馆）。有记录以来累计完成 **141,211 笔有效订单**，总营业额约 **2,034 万泰铢**，全部通过 POS 收银完成，100% 堂食场景。

核心发现：
- ✅ 客单价合理 (144 ฿/单)，符合社区消费定位
- ⚠️ 商品分类混乱：71.6% 的销售额归入"temp"占位品类
- ❌ 成本数据几乎完全缺失（仅 1/276 SKU 有成本记录）
- ❌ 时间戳全部为空，无法做趋势和时段分析
- ⚠️ 退款中 57% 因为"食材不新鲜"
- ❌ 无会员体系、无数字化营销

---

## 二、经营概况

### 2.1 整体指标

| 指标 | 数值 |
|------|------|
| 累计有效订单数 | 141,211 单 |
| 累计营业额 (sales) | 20,344,332.50 ฿ |
| 平均客单价 | 144.07 ฿/单 |
| 退单数 | 223 单 (0.16%) |
| 退单金额 | 35,225 ฿ (0.17%) |
| 取消订单 | 305 单, 70,316 ฿ |
| 创建后未完成 | 14 单, 10,342 ฿ |

### 2.2 订单类型分布

| 类型 | 订单数 | 营业额 | 占比 |
|------|---------|---------|------|
| DINEIN (堂食) | 141,018 | 20,320,729.50 ฿ | 99.9% |
| PICKUP (自取) | 180 | 21,749 ฿ | 0.1% |
| TAKEOUT (外卖) | 13 | 1,854 ฿ | ~0% |

门店几乎全部依赖堂食消费，外卖和自取渠道基本未开发。

### 2.3 渠道分析
- **100% POS 收银**，无任何线上/外卖平台接入
- 无 GrabFood / FoodPanda / LINE MAN 等渠道

---

## 三、商品结构分析

### 3.1 品类结构（已排除 temp 占位品类后的可识别销售额 = 5,774,140 ฿）

| 品类 | 销售额 (฿) | 占比 | SKU数 |
|------|---------------|------|------|
| 🍺 瓶装/罐装啤酒 | 2,187,794 | 37.9% | 51 |
| 🥃 烈酒 | 1,976,814 | 34.3% | 89 |
| 🚬 香烟 | 640,454 | 11.1% | 22 |
| 🍻 生啤/扎啤 | 422,610 | 7.3% | 16 |
| 🍺 整箱啤酒 (ช้างขวดใหญ่) | 244,535 | 4.2% | 2 |
| 🥤 饮料 | 129,791 | 2.3% | 42 |
| 🍶 烧酒 (โซจู) | 88,082 | 1.5% | 11 |
| 🍢 肉丸/小吃 (ลูกชิ้น) | 32,128 | 0.6% | 22 |
| 🥃 尊尼获加黑牌 | 18,456 | 0.3% | 4 |
| 其他 | 33,477 | 0.6% | 16 |
> ⚠️ **temp 品类占位问题严重**: 有 14,569,525 ฿（71.6%）的销售额被归类到 "temp" 占位品类下，说明大量商品未做正确的品类归属。

### 3.2 啤酒类营收合计 = 2,854,939 ฿ (49.4% of 可识别销售)

| 品种 | 销量 (单位) | 销售额 (฿) | 均价 |
|------|---------------|---------------|------|
| 瓶装/罐装啤酒 | 37,727,000 | 2,187,794 | ~58 ฿ |
| 生啤/扎啤 | 583,000 | 422,610 | ~725 ฿ |
| 整箱啤酒 | 353,000 | 244,535 | ~693 ฿ |

### 3.3 TOP 10 单品

| 排名 | 商品 | 品类 | 销售额 (฿) | 客单均价 |
|------|------|------|---------------|------------|
| 1 | ช้างขวดใหญ่ (Chang 大瓶) | 瓶装啤酒 | 567,978 | 59 ฿ |
| 2 | เบียร์ลีโอขวดใหญ่ (Leo 大瓶) | 瓶装啤酒 | 434,122 | 59 ฿ |
| 3 | เบียร์สิงค์ขวดใหญ่ (Singha 大瓶) | 瓶装啤酒 | 417,520 | 68 ฿ |
| 4 | Sangsom กลาง (Sangsom 中瓶) | 烈酒 | 330,750 | 315 ฿ |
| 5 | Sangsom ลิตร (Sangsom 1L) | 烈酒 | 311,880 | 460 ฿ |
| 6 | สิงห์ลัง ขวดใหญ่ (Singha 整箱) | 生啤 | 208,050 | 730 ฿ |
| 7 | SangSom แบน (Sangsom 扁瓶) | 烈酒 | 158,202 | 153 ฿ |
| 8 | HONG THONG กลาง (Hong Thong 中) | 烈酒 | 155,213 | 269 ฿ |
| 9 | ช้างลัง ขวดใหญ่ (Chang 整箱) | 整箱啤酒 | 146,580 | 698 ฿ |
| 10 | L&M red7.1 (香烟) | 香烟 | 141,710 | 74 ฿ |

**核心洞察**: Chang、Leo、Singha 三大啤酒品牌 + Sangsom 朗姆酒是绝对主力产品线。

---

## 四、支付方式分析

| 支付方式 | 笔数 | 金额 (฿) | 金额占比 |
|------------|------|------------|------------|
| 💵 现金 (Cash) | 116,380 | 13,190,726 | 64.7% |
| 📱 QR Code | 24,830 | 4,818,326 | 23.7% |
| 🏦 政府福利卡 (บัตรประชารัฐ) | 7,252 | 2,335,308 | 11.5% |

特点：
- 现金主导意味着找零成本高 (累计找零 10,947,187 ฿)
- QR Code 占比 23.7% 说明已有一定数字化支付基础
- 福利卡客群占比 11.5%，说明有一定低收入/政府补贴消费群体

---

## 五、退款与品质分析

### 5.1 退单原因分布

| 原因 | 笔数 | 金额 (฿) | 占比 |
|------|------|------------|------|
| ไม่สด (不新鲜) | 128 | 35,225 | 57.4% |
| REFUND (其他退款) | 93 | 0 | 41.7% |
| ผิดจุด (出错) | 1 | 0 | 0.4% |
| 无法满足需求 | 1 | 0 | 0.4% |

### 5.2 退菜 TOP 品类
- **temp 占位品类**: 379 笔, 26,806 ฿ (76.1%)
- 具体啤酒/烈酒单品零星退菜, 合计约 8,419 ฿
> 🔴 **品质警报**: 57% 的退款原因是不新鲜，这在餐饮业态中是严重信号，需排查食材供应链和存储环节。

---

## 六、数据质量评估

| 维度 | 状态 | 影响 |
|------|------|------|
| 商品分类 | ❌ 71.6% 在 temp | 无法做品类分析 |
| 成本数据 | ❌ 1/276 SKU | 无法计算毛利 |
| 时间数据 | ❌ 全部为空 | 无法分析趋势/时段 |
| 会员数据 | ❌ 全部为 0 | 无法分析复购/忠诚度 |
| 用餐人数 | ❌ 全部为空 | 无法计算人均消费 |
| 折扣数据 | ❌ 全部为 0 | 无法分析促销效果 |
| 订单/支付/退款数据 | ✅ 完整可用 | 可做基础分析 |

---

## 七、经营建议

### 🔴 紧急 (P0) — 数据基础建设
1. **清理 temp 品类商品**
    - 当前 276 个 SKU 中大量归入 temp，导致 71.6% 销售额无法按品类分析
    - 建议：一周内完成所有 SKU 的品类归属整理，特别是啤酒/烈酒/饮料/食品的正确分类
    - 预期收益：可进行精确的品类销售分析、库存优化、定价策略
2. **补录成本数据**
    - 当前仅 1 个 SKU (temp) 有成本记录，且 cost_price = sell_price (毛利率=0)，数据明显错误
    - 建议：从供应商采购价入手，至少完成 TOP 20 单品的成本录入
    - 预期收益：可计算毛利，识别高利润/亏损产品线
3. **修复时间戳缺失**
    - order_ts、pay_ts、settle_ts 全部为空，stat_date/stat_hour 也为空
    - 建议：排查 POS 系统时间同步，确保后续订单能正确记录时间
    - 预期收益：可做日/周/月趋势分析、高峰时段识别、排班优化

### 🟡 重要 (P1) — 运营优化
4. **建立食品安全标准**
    - "不新鲜" 占退款原因的 57%，直接伤害顾客体验和复购意愿
    - 建议：
        - 检查食材（特别是肉丸/小吃类）的采购频次和存储条件
        - 建立 FIFO (先进先出) 库存轮换制度
        - 对易变质食材标注制作/开封时间
    - 预期收益：降低退款率，提升顾客满意度和口碑
5. **优化支付体验**
    - 现金支付占 64.7%，累计找零超过 1,000 万 ฿，效率低且出错风险高
    - 建议：
        - 在店内显眼位置张贴 QR Code 支付引导
        - 对使用 QR 支付的顾客给予小额优惠（满减/折扣）
        - 推广 PromptPay / TrueMoney Wallet 等泰国主流电子钱包
    - 预期收益：提高收银效率，减少找零错误，沉淀消费数据
6. **建立会员体系**
    - 当前 has_member 全为 0，没有任何会员/忠诚度管理
    - 建议：
        - 先从简单的积分卡开始（纸质也行）：买 10 瓶啤酒免费 1 瓶
        - 针对 Sangsom/Chang 等高频消费品设计积分返利
        - 后续可升级为电子会员 (LINE OA + 积分系统)
    - 预期收益：提升复购率，建立顾客粘性

### 🟢 机会 (P2) — 增长策略
7. **开发外卖/自取渠道**
    - 当前外卖仅 13 单 (0.01%)，在泰国外卖渗透率极高的背景下严重偏低
    - 建议：
        - 入驻 GrabFood / FoodPanda / LINE MAN
        - 重点上线啤酒套餐和烈酒 + 冰块 + 小吃组合
        - 设置外卖专属包装和优惠
    - 预期收益：拓展新客群，提升非高峰时段营收
8. **优化产品组合**
    - 当前小吃/食品类仅占可识别品类的 0.6%
    - 建议：
        - 增加下酒小吃种类（炸鸡、青木瓜沙拉、烤猪肉串等）
        - 设计"啤酒 + 小吃"套餐，提升客单价
        - 引入更多泰国本土精酿啤酒或进口啤酒吸引年轻客群
    - 预期收益：提升客单价，延长顾客停留时间，增加消费额
9. **差异化时段定价**
    - (需先修复时间数据) 建议在 happy hour 时段（如 17:00-19:00）提供啤酒折扣
    - 下午时段可推广非酒精饮品

---

## 八、总结

示例门店 是一家以酒水消费为主的社区型门店，经营基本盘稳定（14 万+ 订单，2,034 万 ฿ 营业额），但目前面临的核心挑战不在销售端，而在 **数据基础设施严重缺失**。

**优先级排序：**
1. 清理商品分类（投入最小，见效最快）
2. 补录关键成本数据（支撑利润分析）
3. 修复时间数据采集（支撑趋势和时段分析）
4. 解决食品安全问题（降低退款率）
5. 推动电子支付和会员体系（提升效率和复购）
6. 探索外卖和产品组合创新（中长期增长）
> ⚠️ **免责声明**: 本报告基于当前数据仓库中 DWD 明细层数据生成，因时间戳、成本、会员等关键维度数据缺失，趋势分析和利润分析无法执行。建议优先完成数据补录后，重新生成完整分析报告。

---

*报告由 Claw AI 经营分析系统自动生成 | 分析人: kejiqing*
`,
};